import { Component } from '@theme/component';

/**
 * Customer reviews carousel. Cards scroll natively (scroll-snap, swipe on touch); the round
 * buttons move one card at a time and are disabled at either end of the track.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CustomerReviewsComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    cancelAnimationFrame(this.#frame);
  }

  /** Scrolls back by one card. */
  previous() {
    this.#scrollByCard(-1);
  }

  /** Scrolls forward by one card. */
  next() {
    this.#scrollByCard(1);
  }

  #listen() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.refs.track.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    window.addEventListener('resize', this.#onScroll, { signal, passive: true });
    this.#updateButtons();
  }

  /** @param {1 | -1} direction */
  #scrollByCard(direction) {
    const { track } = this.refs;
    const card = track.querySelector('.customer-review');
    if (!(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const step = (card.offsetWidth + gap) * direction * (isRtl ? -1 : 1);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({ left: step, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateButtons());
  };

  #updateButtons() {
    const { track, previous, next } = this.refs;
    const position = Math.abs(track.scrollLeft);
    const end = track.scrollWidth - track.clientWidth;

    if (previous) previous.disabled = position <= 2;
    if (next) next.disabled = position >= end - 2;
  }
}

if (!customElements.get('customer-reviews-component')) {
  customElements.define('customer-reviews-component', CustomerReviewsComponent);
}
